import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Checkbox,
  Spinner,
  VStack,
  HStack,
  Image,
  Text,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useDropzone } from 'react-dropzone';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../api';
import { Select as ChakraReactSelect } from "chakra-react-select";


const CreateProduct = ({ isOpen, onClose, onProductCreated, initialData }) => {
  const initialFormState = {
    name: '',
    description: '',
    price: '',
    purchase_price: '',
    stock_quantity: '',
    catalog_number: '',
    unit_measurement: 'шт',
    markup_percentage: 0,
    is_vat: false,
    categories: [],
    country: '',
    images: [],
  };

  const [formData, setFormData] = useState(initialFormState);
  const [categories, setCategories] = useState([[]]); // Top-level categories
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [removedImages, setRemovedImages] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [images, setImages] = useState([]);

  // Fetch categories hierarchy
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await api.get('/categories/hierarchy/');
      setCategories([response.data]); // Initialize with top-level categories
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (initialData) {
      // Populate form data with initialData
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        purchase_price: initialData.purchase_price || '',
        stock_quantity: initialData.stock_quantity || '',
        catalog_number: initialData.catalog_number || '',
        unit_measurement: initialData.unit_measurement || 'шт',
        markup_percentage: initialData.markup_percentage || 0,
        is_vat: initialData.is_vat || false,
        categories: initialData.categories || [],
        country: initialData.country || '',
        images: initialData.images || [],
      });
  
      // Populate images from initialData
      const processedImages =
        initialData.images?.map((image, index) => ({
          file: null,
          preview: image.preview,
          id: image.id,
          priority: image.priority || index + 1,
        })) || [];
      setImages(processedImages);
  
      // Initialize categories
      const fetchInitialCategories = async () => {
        try {
          const topLevelCategoriesResponse = await api.get('/categories/hierarchy/');
          setCategories([topLevelCategoriesResponse.data]);
  
          if (initialData.categories.length) {
            const resolvedCategories = [];
            for (const categoryId of initialData.categories) {
              const categoryResponse = await api.get(`/categories/${categoryId}/`);
              const category = categoryResponse.data;
  
              resolvedCategories.push(category);
              if (category.children && category.children.length > 0) {
                setCategories((prev) => [...prev, category.children]);
              }
            }
            setSelectedCategories(initialData.categories);
          }
        } catch (error) {
          console.error('Failed to fetch initial categories:', error);
        }
      };
  
      fetchInitialCategories();
    } else {
      // Reset state for creation mode
      setFormData(initialFormState);
      setSelectedCategories([]);
      setImages([]);
      setCategories([[]]);
    }
  }, [initialData]);
  
  

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await api.get('/countries/');
        setCountries(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        setCountries([]); // Fallback to an empty array
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleClose = () => {
    // Reset all form fields, categories, and images
    setFormData(initialFormState);
    setSelectedCategories([]);
    setImages([]);
    fetchCategories(); // Reload categories for top-level options
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCategoryChange = async (categoryId, level) => {
    const selected = [...selectedCategories];
    selected[level] = categoryId;
    setSelectedCategories(selected.slice(0, level + 1));
    setFormData({ ...formData, categories: selected });
  
    // Fetch children for the newly selected category
    try {
      const response = await api.get(`/categories/${categoryId}/`);
      const parent = response.data;
  
      if (parent.children && parent.children.length > 0) {
        const updatedCategories = [...categories];
        updatedCategories[level + 1] = parent.children; // Set children as the next level
        setCategories(updatedCategories.slice(0, level + 2));
      } else {
        setCategories(categories.slice(0, level + 1)); // Clear further levels if no children
      }
    } catch (error) {
      console.error('Failed to fetch child categories:', error);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (images.length + acceptedFiles.length > 6) {
      alert('You can only upload up to 6 images.');
      return;
    }
    const newImages = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...newImages]);
  };

  const reorderImages = (result) => {
    if (!result.destination) return;
  
    const reorderedImages = Array.from(images);
    const [movedImage] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, movedImage);
  
    // Update priority based on the new order
    const updatedImages = reorderedImages.map((image, index) => ({
      ...image,
      priority: index + 1,
    }));
  
    setImages(updatedImages);
  };
  

  const removeImage = (index) => {
    setImages((prev) => {
      const removed = [...prev];
      const [removedImage] = removed.splice(index, 1);
  
      if (removedImage.id) {
        // If the image exists on the backend, add it to removedImages
        setRemovedImages((prevRemoved) => [...prevRemoved, removedImage.id]);
      }
  
      return removed;
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
      };
  
      const response = initialData
        ? await api.put(`/products/${initialData.id}/`, payload)
        : await api.post('/products/', payload);
  
      const productId = response.data.id;
  
      const formDataToSend = new FormData();
  
      // Handle adding new images
      images.forEach((image, index) => {
        if (image.file) {
          formDataToSend.append(`image_${index + 1}`, image.file);
          formDataToSend.append(`priority_image_${index + 1}`, image.priority || index + 1);
        }
      });
  
      // Handle removed images
      if (removedImages.length > 0) {
        formDataToSend.append('remove_images', removedImages.join(','));
      }
  
      // Send formData to manage-images API
      if (images.length > 0 || removedImages.length > 0) {
        formDataToSend.append('product', productId);
  
        await api.post('/product-images/manage-images/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
  
      // Handle reordering priorities
      const reorderedImages = images.filter((image) => !image.file); // Exclude newly added images
      for (const image of reorderedImages) {
        await api.patch(`/product-images/${image.id}/set-priority/`, {
          priority: image.priority,
        });
      }
  
      onProductCreated();
      handleClose(); // Clear fields and close popup
    } catch (error) {
      console.error('Failed to create/update product:', error);
    }
  };
  
  const handlePurchasePriceChange = (e) => {
    const purchase_price = parseFloat(e.target.value) || 0;
    const markup_percentage = parseFloat(formData.markup_percentage) || 0;
    const price = purchase_price + (purchase_price * (markup_percentage / 100));
    setFormData((prev) => ({
      ...prev,
      purchase_price: e.target.value,
      price: price.toFixed(2),
    }));
  };

  const handleMarkupChange = (e) => {
    const inputValue = e.target.value;
    const markup_percentage = inputValue === '' ? 0 : parseFloat(inputValue);
    const purchase_price = parseFloat(formData.purchase_price) || 0;
    const price = purchase_price + (purchase_price * (markup_percentage / 100));

    setFormData((prev) => ({
      ...prev,
      markup_percentage: inputValue, // keep what user types
      price: price.toFixed(2),
    }));
  };

  const handlePriceChange = (e) => {
    const price = parseFloat(e.target.value) || 0;
    const purchase_price = parseFloat(formData.purchase_price) || 0;

    const markup_percentage =
      purchase_price > 0 ? ((price - purchase_price) / purchase_price) * 100 : 0;

    setFormData((prev) => ({
      ...prev,
      price: e.target.value,
      markup_percentage: markup_percentage.toFixed(2),
    }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop,
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" h="90vh" overflowY="auto">
        <ModalHeader>{initialData ? 'Редагувати товар' : 'Створити новий товар'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex>
            {/* Left Side */}
            <Box flex="0 0 400px" borderRight="1px solid #e2e8f0" pr="4">
              <FormControl>
                <FormLabel>Назва</FormLabel>
                <Input name="name" value={formData.name} onChange={handleChange} />
              </FormControl>

              <FormControl mt="4">
              <FormLabel>Категорії</FormLabel>
              {categories.map((categoryList, level) => (
                <Box key={level} mb={2}>
                  <Select
                    placeholder="Оберіть категорію"
                    onChange={(e) => handleCategoryChange(parseInt(e.target.value), level)}
                    value={selectedCategories[level] || ''}
                  >
                    {Array.isArray(categoryList) &&
                      categoryList.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </Select>
                </Box>
              ))}
            </FormControl>

            </Box>

            {/* Right Side */}
            <Box flex="0 0 700px" pl="6">
              <FormControl mb="4">
                <FormLabel>Опис</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </FormControl>

              <HStack spacing="4" mb="4">
                <FormControl>
                  <FormLabel>Каталожний номер</FormLabel>
                  <Input
                    name="catalog_number"
                    value={formData.catalog_number}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Кількість на складі</FormLabel>
                  <Input
                    type="number"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Закупочна ціна</FormLabel>
                  <Input
                    type="number"
                    name="purchase_price"
                    value={formData.purchase_price}
                    onChange={handlePurchasePriceChange}
                />
                </FormControl>
                <FormControl>
                  <FormLabel>Відсоток націнки</FormLabel>
                  <Input
                    type="number"
                    name="markup_percentage"
                    value={formData.markup_percentage}
                    onFocus={(e) => {
                      if (e.target.value === "0") e.target.select();
                    }}
                    onBlur={(e) => {
                      if (e.target.value === "") {
                        setFormData((prev) => ({ ...prev, markup_percentage: 0 }));
                      }
                    }}
                    onChange={handleMarkupChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Одиниця виміру</FormLabel>
                  <Select
                    name="unit_measurement"
                    value={formData.unit_measurement}
                    onChange={handleChange}
                  >
                    <option value="шт">шт</option>
                    <option value="л">л</option>
                  </Select>
                </FormControl>
              </HStack>

              <HStack spacing="4">
                <FormControl>
                  <FormLabel>Ціна</FormLabel>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handlePriceChange}
                />
                </FormControl>
                <FormControl>
                  <FormLabel>Країна виробник</FormLabel>
                  {loadingCountries ? (
                    <Spinner />
                  ) : (
                    <ChakraReactSelect
                      name="country"
                      value={countries.find(c => c.id === formData.country) || null}
                      onChange={(selectedOption) =>
                        setFormData({ ...formData, country: selectedOption ? selectedOption.id : '' })
                      }
                      options={countries}
                      getOptionLabel={(e) => e.name}
                      getOptionValue={(e) => e.id}
                      placeholder="Оберіть країну"
                      isClearable
                    />
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>Податок</FormLabel>
                  <Checkbox
                    name="is_vat"
                    isChecked={formData.is_vat}
                    onChange={handleChange}
                  >
                    З ПДВ
                  </Checkbox>
                </FormControl>
              </HStack>

              {/* Images Section Below */}
              <Box mt="6">
                <FormControl>
                <DragDropContext onDragEnd={reorderImages}>
                  <Droppable droppableId="images" direction="horizontal">
                    {(provided) => (
                      <HStack
                        mt={4}
                        spacing={4}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        overflowX="auto"
                      >
                        {images.map((image, index) => (
                          <Draggable
                            key={image.file ? image.file.name : image.id || index}
                            draggableId={image.file ? image.file.name : String(image.id || index)}
                            index={index}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                position="relative"
                                borderRadius="md"
                                border="1px solid #e2e8f0"
                                overflow="hidden"
                                boxSize="100px"
                              >
                                <Image
                                  src={image.preview}
                                  alt={`Preview ${index}`}
                                  boxSize="100px"
                                  objectFit="cover"
                                />
                                <IconButton
                                  icon={<CloseIcon />}
                                  size="sm"
                                  position="absolute"
                                  top="2px"
                                  right="2px"
                                  onClick={() => removeImage(index)}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </HStack>
                    )}
                    </Droppable>
                  </DragDropContext>
                  <Box
                    {...getRootProps()}
                    border="2px dashed #3182ce"
                    p="4"
                    textAlign="center"
                    cursor="pointer"
                    borderRadius="md"
                    bg="blue.50"
                    mt="4"
                  >
                    <input {...getInputProps()} />
                    <Text color="blue.500">Перетягніть або натисніть для вибору зображень</Text>
                  </Box>
                </FormControl>
              </Box>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
            {initialData ? 'Редагувати' : 'Створити'}
          </Button>
          <Button variant="ghost" onClick={handleClose}>
            Скасувати
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateProduct;
